import React, { FC, forwardRef } from 'react';
import Link from 'next/link';
import { Body, spacing } from '@sumup/circuit-ui';
import { ChevronRight } from '@sumup/icons';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import {
  ReadArticleLink,
  ArticleDescription,
  SectionArticlesItem,
} from '../styled';

import {
  SectionTree,
  SectionArticles as SectionArticleTypes,
} from 'types/common';
import { addSlugToUrl } from 'services/common';
import { ArticleTitle, PageContentTitle } from 'components/styledComponents';
import { SUBPATH_TYPES } from 'constants/common';

type SectionArticlesProps = {
  articles: SectionArticleTypes;
  isPos?: boolean;
  sectionTree: SectionTree;
  currentSection: string;
};

// eslint-disable-next-line prefer-arrow-callback
const ForwardedReadArticleLink = forwardRef(function CustomReadArticleLink(
  { href, t }: { href?: string; t: (param: string) => string },
  /* eslint-disable @typescript-eslint/no-unused-vars */
  _ref,
) {
  return (
    <ReadArticleLink href={href}>
      <Body variant="highlight">{t('read_article')}</Body>
      <ChevronRight />
    </ReadArticleLink>
  );
});

const SectionArticles: FC<SectionArticlesProps> = ({
  articles,
  isPos = false,
  sectionTree = {},
  currentSection = '',
}) => {
  const { t } = useTranslation();

  return sectionTree[currentSection] ? (
    <>
      {sectionTree[currentSection]?.sectionImage ? (
        <Image
          src={`https:${sectionTree[currentSection]?.sectionImage?.url}`}
          alt={sectionTree[currentSection].sectionImage.name}
          width={640}
          height={260}
        />
      ) : null}
      <PageContentTitle as="h1" size="two" css={spacing({ top: 'peta' })}>
        {sectionTree[currentSection].sectionTitle}
      </PageContentTitle>
      {Object.keys(articles).map((articleId) => (
        <SectionArticlesItem key={articleId}>
          <ArticleTitle as="h2" size="four">
            {articles[articleId].title}
          </ArticleTitle>
          <ArticleDescription>
            {articles[articleId].articleDescription}
          </ArticleDescription>
          <Link
            passHref
            href={addSlugToUrl({
              subPath: SUBPATH_TYPES.articles,
              contentId: articleId,
              contentSlug: articles[articleId].articleSlug,
              isPos,
            })}
          >
            <ForwardedReadArticleLink t={t} />
          </Link>
        </SectionArticlesItem>
      ))}
    </>
  ) : null;
};

export default SectionArticles;
